import React from 'react';
import { MoreVertical } from 'lucide-react';

/**
 * Layout-matching skeleton for Profile first paint.
 * Mirrors current card structure (not legacy ProfileLoadingOverlay).
 */
export function ProfilePageSkeleton(): React.ReactElement {
  return (
    <div className="profile-page-container w-full bg-gradient-to-b from-[#FAF7F5] to-[#F3EDE8] dark:bg-gradient-to-br dark:from-blue-900 dark:via-purple-900 dark:to-indigo-900">
      <div className="profile-content-area pb-[88px]">
        <div className="max-w-sm sm:max-w-md md:max-w-3xl mx-auto space-y-3 md:space-y-0 md:py-4 px-3">
          {/* Profile header card */}
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border border-[#E8E0DA]/50 dark:border-white/20 rounded-2xl sm:rounded-3xl p-3 sm:p-4 shadow-[0_2px_16px_rgba(120,80,60,0.06)] dark:shadow-2xl relative profile-card">
            <div className="absolute top-2 sm:top-3 left-2 sm:left-3 right-2 sm:right-3 flex justify-between items-center z-10">
              <div className="bg-green-500/20 border border-green-500/30 rounded-full px-3 py-1.5 sm:py-2 min-w-[50px] min-h-[32px] animate-pulse" />
              <div className="text-[#6B6560] p-1.5 sm:p-2 rounded-full">
                <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5 opacity-30" aria-hidden="true" />
              </div>
            </div>

            <div className="flex flex-col pt-6 pb-3 md:hidden">
              <div className="flex justify-center items-center mb-4 w-full">
                <div className="w-full max-w-[280px] sm:max-w-[340px]">
                  <div className="aspect-[4/5] w-full rounded-xl bg-[#E8E0DA]/60 dark:bg-white/10 animate-pulse" />
                </div>
              </div>
              <div className="h-8 w-40 mx-auto rounded-lg bg-[#E8E0DA]/60 dark:bg-white/10 animate-pulse" />
            </div>

            <div className="hidden md:flex flex-1 min-h-[320px] justify-center items-center w-full">
              <div className="w-full max-w-[320px] aspect-[4/5] rounded-xl bg-[#E8E0DA]/60 dark:bg-white/10 animate-pulse" />
            </div>
          </div>

          {/* Stats card */}
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border border-[#E8E0DA]/50 dark:border-white/20 rounded-2xl sm:rounded-3xl p-3 sm:p-4 md:p-2 shadow-[0_2px_16px_rgba(120,80,60,0.06)] dark:shadow-2xl profile-card">
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="backdrop-blur-sm bg-[#F8F4F1] dark:bg-white/5 border border-[#E8E0DA]/50 dark:border-white/10 rounded-xl p-2 sm:p-3 text-center min-h-[88px]"
                >
                  <div className="bg-[#E8E0DA] dark:bg-white/10 rounded-full h-10 w-10 mx-auto mb-2 animate-pulse" />
                  <div className="bg-[#E8E0DA] dark:bg-white/10 rounded-lg h-6 w-12 mx-auto mb-1 animate-pulse" />
                  <div className="bg-[#E8E0DA] dark:bg-white/10 rounded-lg h-4 w-16 mx-auto animate-pulse" />
                </div>
              ))}
            </div>
          </div>

          {/* Founding card placeholder */}
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border-2 border-pink-300/50 dark:border-pink-500/30 rounded-2xl sm:rounded-3xl p-4 min-h-[128px] profile-card animate-pulse">
            <div className="h-full flex items-center justify-center">
              <div className="h-4 w-32 rounded bg-[#E8E0DA]/60 dark:bg-white/10" />
            </div>
          </div>

          {/* CTA placeholder */}
          <div className="w-full">
            <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border border-[#E8E0DA]/50 dark:border-white/20 rounded-2xl sm:rounded-3xl p-3 shadow-[0_2px_16px_rgba(120,80,60,0.06)]">
              <div className="w-full min-h-[48px] rounded-2xl bg-[#E8E0DA]/60 dark:bg-white/10 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePageSkeleton;
